import { Outlet, Link, useLocation } from 'react-router-dom';
import { AgentPhone } from '@features/voice/components/AgentPhone';
import { BridgePhone } from '@features/voice/components/BridgePhone';
import { OutboundCallPhone } from '@features/voice/components/OutboundCallPhone';
import { IncomingCallModal } from '@features/voice/components/IncomingCallModal';
import { CallNotice } from '@features/voice/components/CallNotice';
import { WrapUpDialog } from '@features/voice/components/WrapUpDialog';
import { ActiveCallBar } from '@features/voice/components/ActiveCallBar';
import { useToast } from '@centraoneplus/ui';
import { useAuthStore } from '@stores/authStore';
import { useSettingsStore } from '@stores/settingsStore';
import { ThemeToggle } from '@components/ThemeToggle';
import {
  LogOut, LayoutDashboard, Inbox as InboxIcon, Ticket, History,
  MessageSquare, MessageCircleMore, Facebook, Phone, Users, BookOpen, BarChart3, Plug, Settings as SettingsIcon,
  BookMarked, ChevronsLeft, ChevronsRight, type LucideIcon,
} from 'lucide-react';
import { useHoverPeek, useNavCollapsed } from '@components/layouts/useNavRail';
import { useNavCounts } from '@components/layouts/useNavCounts';
import { useLiveSocialChannels, type RailSocialChannel } from '@components/layouts/useLiveSocialChannels';
import { useScrollFades } from '@components/layouts/useScrollFades';
import { AccountMenu } from '@components/layouts/AccountMenu';
import { formatBadgeCount, navBadgeFor } from '@components/layouts/navBadges';
import { useBreakpoint } from '@shared/hooks/useBreakpoint';
import { useCallback, useState, useEffect } from 'react';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { BrandLogo } from '@components/brand/BrandLogo';
import { PageHeaderMetaRegistry } from '@components/layouts/pageHeaderMetaRegistry';

/**
 * Application shell: the nav rail and the content column.
 *
 * The rail is a floating card (--co-rail) whose width follows the breakpoint
 * tier: 232px labelled at `full`, 186px labelled at `laptop`, and a 52px glyph
 * rail below 1200. It is always on screen — there is no drawer, so pages have
 * no menu button to open one. Above 1200 the user can collapse it to glyphs
 * (remembered in their profile, see useNavRail); a collapsed rail peeks open
 * over the page on hover or keyboard focus.
 * - Labelled items are an icon + label; the active item gets a 2px left marker and
 *   the selected surface (.co-nav-item). Items are grouped in `navGroups`, but
 *   the group names are not printed.
 * - Count badges (useNavCounts / navBadges) on Tickets and Live Chat:
 *   a pill at the row's end, or a corner badge on the glyph; red only when
 *   something counted is past SLA. A badged item links to what it counted.
 * - Header: the official logo (just the "1" mark in the glyph rail).
 * - Footer: avatar, name, role in the metadata voice, theme toggle, sign out.
 *
 * There is no global top bar; each page header carries its own status + bell
 * cluster (PageHeaderMeta), with a fallback drawn here for screens that don't.
 * The shell also mounts the voice components and the urgent-notification toasts.
 */

// Each item's icon is drawn in both rails: beside the label in the labelled
// rail (muted, so the words still lead the scan) and alone in the glyph rail.
type NavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Routes outside `path` that belong to this item and should light it up. */
  alsoActiveFor?: string[];
  /** Shown only while this social channel is configured and enabled. */
  channel?: RailSocialChannel;
};
type NavGroup = { label: string; items: NavItem[] };

/** Whether a rail item is the current section for this pathname. */
export function isNavItemActive(
  item: Pick<NavItem, 'path' | 'alsoActiveFor'>,
  pathname: string,
): boolean {
  return [item.path, ...(item.alsoActiveFor ?? [])].some((p) => pathname.startsWith(p));
}

const navGroups: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { path: '/dashboard', label: 'Dashboard' , icon: LayoutDashboard },
      { path: '/inbox', label: 'Inbox' , icon: InboxIcon },
      { path: '/tickets', label: 'Tickets' , icon: Ticket },
      { path: '/backtrack', label: 'Backtrack' , icon: History },
    ],
  },
  {
    label: 'Channels',
    items: [
      { path: '/live-chat', label: 'Live Chat' , icon: MessageSquare },
      { path: '/live-call', label: 'Live Call' , icon: Phone },
      { path: '/whatsapp', label: 'WhatsApp' , icon: MessageCircleMore, channel: 'whatsapp' },
      { path: '/facebook', label: 'Facebook' , icon: Facebook, channel: 'facebook' },
    ],
  },
  {
    label: 'Records',
    items: [
      { path: '/customers', label: 'Customers' , icon: Users },
      // `/organizations` is deliberately NOT in the nav. The route exists and
      // the page works; the way in is the Customers header's PEOPLE /
      // ORGANISATIONS control, which is where the two record types are already
      // presented as one pair.
      { path: '/knowledge', label: 'Knowledge Base' , icon: BookOpen },
      { path: '/reports', label: 'Reports' , icon: BarChart3 },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/integrations', label: 'Integrations' , icon: Plug },
      // The audit log is an admin screen reached from Settings; it has no rail
      // entry of its own, so Settings is the item that stays lit on it.
      { path: '/settings', label: 'Settings' , icon: SettingsIcon, alsoActiveFor: ['/audit-logs'] },
      { path: '/help', label: 'User Guide' , icon: BookMarked },
    ],
  },
];

/**
 * Phase 34 floating shell. The rail stops being a wall butted against the
 * viewport edge and becomes a card sitting ON the canvas, the same way every
 * other card on the page does.
 *
 * RAIL_INSET is the breathing room around the card; RAIL_GAP is the channel
 * between it and the content.
 */
const RAIL_INSET = 16;
const RAIL_GAP = 14;
const GLYPH_WIDTH = 52;

export default function MainLayout() {
  // Phase 18 §3.1 — the app nav gives up its labels first, because it is where
  // you came from; the in-page rail is what you are navigating now. Below 1200
  // the rail is always glyphs; above it, the user can collapse it themselves.
  const tier = useBreakpoint();
  const autoGlyph = tier === 'tabletL' || tier === 'tabletP';
  const { collapsed: userCollapsed, toggle: toggleCollapsed } = useNavCollapsed();
  const collapsed = autoGlyph || userCollapsed;
  // A collapsed rail opens OVER the page on hover / focus ("peek"); the content
  // keeps its collapsed inset, so nothing on the page moves.
  // While the glyph rail's account menu is open the rail must not peek open
  // underneath it (that would swap the footer and drop the menu).
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const { peek, close: closePeek, handlers: peekHandlers } = useHoverPeek(collapsed && !accountMenuOpen);
  const glyphNav = collapsed && !peek;
  // Fade the list's edges when it scrolls (short screens), so a cut-off icon
  // reads as "more below", not as merged into the footer.
  const { ref: navScrollRef, fades: navFades } = useScrollFades<HTMLElement>([glyphNav]);
  const navCounts = useNavCounts();
  const liveChannels = useLiveSocialChannels();
  const labelledWidth = tier === 'laptop' ? 186 : 232;
  const navWidth = glyphNav ? GLYPH_WIDTH : labelledWidth;
  const reservedWidth = collapsed ? GLYPH_WIDTH : labelledWidth;
  // The 186px laptop rail leaves the name ~40px beside the avatar and the two
  // controls, which truncated even a seven-letter name; tighter gaps and
  // control padding there give it room.
  const tightIdentity = !glyphNav && tier === 'laptop';
  const [loggingOut, setLoggingOut] = useState(false);
  // If the avatar image fails to load, fall back to the name initial.
  const [avatarError, setAvatarError] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const toast = useToast();

  // Retry showing the image when the avatar URL changes (e.g. after upload).
  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatarUrl]);

  // #18929 — seed the signed-in user's preferred display timezone once after
  // login, so every screen's times honour Settings → Account → Timezone from
  // the first load (not just after visiting Settings). fetchProfile sets it via
  // the settings store; skip if the profile is already loaded.
  useEffect(() => {
    const store = useSettingsStore.getState();
    if (!store.profile) void store.fetchProfile();
  }, []);

  // Listen for urgent notifications and show toast alerts
  useEffect(() => {
    const handleUrgentNotification = (event: CustomEvent) => {
      const { type, title, message, originalType, severity } = event.detail;

      // Show toast based on notification type and severity
      if (originalType === 'sla_breach') {
        // For SLA breaches, use severity to determine toast style
        if (severity === 'critical') {
          toast.error(`[CRITICAL] ${title}`, message);
        } else if (severity === 'major') {
          toast.error(title, message);
        } else {
          toast.warning(title, message);
        }
      } else if (type === 'error') {
        toast.error(title, message);
      } else if (type === 'warning') {
        toast.warning(title, message);
      } else {
        toast.info(title, message);
      }
    };

    window.addEventListener('urgent-notification', handleUrgentNotification as EventListener);
    return () => {
      window.removeEventListener('urgent-notification', handleUrgentNotification as EventListener);
    };
  }, [toast]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      // A full document load, not `navigate('/login')`. Client-side routing
      // keeps the module graph alive, so every non-persisted zustand store
      // (tickets, customers, inbox, settings, …) would still hold this
      // organization's data when the next user signs in in this tab. The
      // "signed out" confirmation moves to the login page's notice banner.
      window.location.assign('/login?reason=signed_out');
    } catch (err: any) {
      toast.error(
        'Sign out failed',
        err?.response?.data?.message ||
          err?.response?.data?.error?.message ||
          err?.message ||
          'Please try again.',
      );
      // Re-enable the button so the user can retry (success navigates away).
      setLoggingOut(false);
    }
  };

  const roleLabel = user?.role?.replace(/[_-]/g, ' ');

  // The avatar face, shared by the labelled row and the glyph rail's menu
  // trigger. The name is printed or announced beside it, so the image is decorative.
  const avatarFace =
    user?.avatarUrl && !avatarError ? (
      <img
        src={user.avatarUrl}
        alt=""
        className="w-[30px] h-[30px] object-cover"
        onError={() => setAvatarError(true)}
      />
    ) : (
      <span className="text-xs font-semibold">{user?.name?.charAt(0).toUpperCase()}</span>
    );

  // How many PageHeaderMeta clusters the current screen renders itself. At
  // zero, the shell draws one top-right so no screen goes without it.
  const [headerMetaCount, setHeaderMetaCount] = useState(0);
  const registerHeaderMeta = useCallback(() => {
    setHeaderMetaCount((n) => n + 1);
    return () => setHeaderMetaCount((n) => n - 1);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--co-canvas)' }}>
      <AgentPhone />
      <BridgePhone />
      <OutboundCallPhone />
      <IncomingCallModal />
      <CallNotice />
      <WrapUpDialog />
      <ActiveCallBar />

      {/* Nav rail. Always on screen: below 1200 it is the glyph rail, so the
          labelled rail never needs to slide away as a drawer. */}
      <div
        className="group fixed inset-y-0 left-0 z-50 transition-[width] duration-200 ease-out motion-reduce:transition-none"
        data-nav-tier={tier}
        data-nav-collapsed={collapsed || undefined}
        data-nav-peek={peek || undefined}
        style={{
          width: navWidth + RAIL_INSET,
          padding: `${RAIL_INSET}px 0 ${RAIL_INSET}px ${RAIL_INSET}px`,
        }}
        {...peekHandlers}
      >
        {/* Collapse / expand. Sits on the card's right edge, below the logo,
            so it fits every rail width; shown on hover or focus (always on
            touch screens). Below 1200 the rail is glyphs regardless, so there
            is nothing to toggle. */}
        {!autoGlyph && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            aria-controls="app-nav"
            className="absolute z-10 flex h-6 w-6 items-center justify-center rounded-full opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
            style={{
              top: RAIL_INSET + 48,
              left: RAIL_INSET + navWidth - 12,
              background: 'var(--co-page)',
              border: '1px solid var(--co-line-strong)',
              color: 'var(--co-ink-3)',
              boxShadow: 'var(--co-shadow-pop)',
            }}
          >
            {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <ChevronsLeft className="h-3.5 w-3.5" />}
          </button>
        )}
        <div
          data-nav-rail-card
          className="flex flex-col h-full overflow-hidden"
          style={{
            // The rail's own grey (--co-rail). That grey sits close to the
            // canvas, so the panel gets the strong hairline and the deeper
            // pop shadow to lift it clearly off the page (theme-aware in both
            // light and dark).
            background: 'var(--co-rail)',
            border: '1px solid var(--co-line-strong)',
            borderRadius: 14,
            boxShadow: 'var(--co-shadow-pop)',
          }}
        >
          {/* Wordmark */}
          <div
            className={`flex flex-none items-center pb-[14px] pt-4 ${glyphNav ? 'justify-center px-0' : 'px-4'}`}
            style={{ borderBottom: '1px solid var(--co-line-subtle)' }}
          >
            {/* Official logo — colour in light mode, white in dark mode; the
                collapsed rail shows just the "1" symbol. */}
            <div className="flex h-[28px] min-w-0 items-center">
              {glyphNav ? <BrandLogo height={28} mark /> : <BrandLogo height={26} />}
            </div>
          </div>

          {/* Navigation. The scrollbar is hidden (see .co-scrollbar-none) —
              on a 232px card a permanent gutter reads as a defect. */}
          <nav
            id="app-nav"
            aria-label="Main"
            ref={navScrollRef}
            data-fade-top={navFades.top || undefined}
            data-fade-bottom={navFades.bottom || undefined}
            className={`co-nav-scroll flex-1 overflow-y-auto co-scrollbar-none ${glyphNav ? 'pb-2 pt-1.5' : 'pb-3 pt-2'}`}
          >
            {navGroups.map((group) => (
              <div key={group.label}>
                {/* Section headings are dropped in BOTH nav widths, not just
                    the glyph rail: the labels named the grouping twice over,
                    once in words and once in the gap above each run.
                    `group.label` stays as the React key and as the accessible
                    name source for the glyph rail's tooltips.

                    The extra gap the groups used to open above each run is
                    dropped too. With the headings gone it had nothing left to
                    label, so it read as three uneven spaces in one list rather
                    than as structure — the grouping was legible only to
                    whoever wrote it. Every item is now evenly spaced. */}
                {group.items.filter((item) => !item.channel || liveChannels[item.channel]).map((item) => {
                  const isActive = isNavItemActive(item, location.pathname);
                  // While an item has a badge it links to exactly what was
                  // counted, and its name says the count.
                  const badge = navBadgeFor(item.path, navCounts);
                  const to = badge?.to ?? item.path;
                  const accessibleName = badge ? `${item.label}, ${badge.label}` : item.label;
                  const badgeClass = badge?.urgent ? ' is-urgent' : '';
                  if (glyphNav) {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={to}
                        title={accessibleName}
                        aria-label={accessibleName}
                        aria-current={isActive ? 'page' : undefined}
                        className="mx-auto my-px flex h-8 w-9 items-center justify-center"
                        style={
                          isActive
                            ? {
                                background: 'var(--co-selected, var(--co-page))',
                                // inset, not border-left: a border would shift
                                // the glyph by a pixel against its neighbours.
                                boxShadow: 'inset 2px 0 0 var(--co-ink)',
                                color: 'var(--co-ink)',
                              }
                            : { color: 'var(--co-ink-3)' }
                        }
                      >
                        <span className="relative flex">
                          <Icon className="h-[17px] w-[17px]" strokeWidth={1.7} />
                          {badge && (
                            <span className={`co-nav-badge${badgeClass}`} aria-hidden="true">
                              {formatBadgeCount(badge.count)}
                            </span>
                          )}
                        </span>
                      </Link>
                    );
                  }
                  return (
                    <Link
                      key={item.path}
                      to={to}
                      className={`co-nav-item ${isActive ? 'is-active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={badge ? accessibleName : undefined}
                      title={badge?.label}
                      onClick={peek ? closePeek : undefined}
                    >
                      <item.icon className="co-nav-icon" strokeWidth={1.7} aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                      {badge && (
                        <span className={`co-nav-count${badgeClass}`} aria-hidden="true">
                          {formatBadgeCount(badge.count)}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Identity. The glyph rail is 52px wide, so there it is just the
              avatar, which opens the account menu (theme, sign out); stacking
              all three took ~120px and pushed the last nav icons into the
              footer on short screens. The labelled rail keeps one row. */}
          {glyphNav ? (
            <div
              className="flex flex-none justify-center py-3"
              style={{ borderTop: '1px solid var(--co-line-subtle)' }}
            >
              <AccountMenu
                avatar={avatarFace}
                name={user?.name}
                detail={roleLabel || user?.email}
                email={user?.email}
                onSignOut={handleLogout}
                signingOut={loggingOut}
                onOpenChange={setAccountMenuOpen}
              />
            </div>
          ) : (
          <div
            className={`flex flex-none items-center px-3.5 py-3 ${tightIdentity ? 'gap-1.5' : 'gap-2.5'}`}
            style={{ borderTop: '1px solid var(--co-line-subtle)' }}
          >
            <div
              className="w-[30px] h-[30px] shrink-0 overflow-hidden flex items-center justify-center"
              style={{ background: 'var(--co-avatar)', color: 'var(--co-ink-invert)', borderRadius: 8 }}
            >
              {avatarFace}
            </div>
            <div className="min-w-0 flex-1">
              {/* Two lines, not one: beside the avatar and two controls the rail
                  leaves about 95px, which cut a 14-letter name to "Bhuvan keyn…". */}
              <p
                className="line-clamp-2 break-words text-[12.5px] font-semibold leading-[1.25]"
                style={{ color: 'var(--co-ink)' }}
                title={user?.name}
              >
                {user?.name}
              </p>
              <p
                className="truncate mt-[2px]"
                style={{
                  fontFamily: 'var(--co-font-mono)',
                  fontSize: '9.5px',
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--co-ink-3)',
                }}
                title={user?.email}
              >
                {roleLabel || user?.email}
              </p>
            </div>
            <div className="flex items-center shrink-0">
              {/* Both controls share ThemeToggle's 16px icon and hover styling,
                  so the pair reads as one set. */}
              <ThemeToggle className={tightIdentity ? '!p-1.5' : ''} iconClassName="w-4 h-4" />
              <button
                type="button"
                onClick={handleLogout}
                disabled={loggingOut}
                className={`${tightIdentity ? 'p-1.5' : 'p-2'} rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors`}
                title="Sign out"
                aria-label="Sign out"
                aria-busy={loggingOut}
              >
                <LogOut className={`w-4 h-4 ${loggingOut ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Main content. The 52px global bar is gone — it carried two controls
          for every route; each page header now carries its own utility cluster
          (see PageHeaderMeta). Its stacking note went with it: the notification
          dropdown now lives inside a page header, so stacking is that header's
          problem, not the shell's. */}
      <div
        className="h-screen flex flex-col transition-[padding] duration-200 ease-out motion-reduce:transition-none"
        style={{ paddingLeft: RAIL_INSET + reservedWidth + RAIL_GAP }}
      >
        <main className="flex-1 min-h-0 flex flex-col" style={{ background: 'var(--co-canvas)' }}>
          {/* Fallback for screens whose header has no status + bell. Screens
              that render PageHeaderMeta themselves register and suppress it. */}
          {headerMetaCount === 0 && (
            <div className="flex flex-none justify-end px-4 pt-3">
              <PageHeaderMeta isFallback />
            </div>
          )}
          <PageHeaderMetaRegistry.Provider value={registerHeaderMeta}>
            {/* min-w-0 is the horizontal analog of min-h-0 above: without it,
                a flex-column item's default min-width:auto lets a wide
                fixed-layout child (e.g. TicketsPage's table-fixed list, Bug
                #9010) refuse to shrink below its content's intrinsic width,
                pushing the whole page wider than the viewport instead of
                scrolling inside its own container. */}
            <div className="flex-1 min-h-0 min-w-0">
              <Outlet />
            </div>
          </PageHeaderMetaRegistry.Provider>
        </main>
      </div>
    </div>
  );
}